import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type { Contact } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { CircleAlert, Clock3, ShieldCheck } from "lucide-react";

export function getContactPortalEmail(portalAccess: Contact["portalAccess"]) {
  const status = portalAccess?.activeConnection
    ? "active"
    : portalAccess?.status;
  if (status !== "active" && status !== "pending" && status !== "expired") {
    return "";
  }
  return (
    (status === "active"
      ? (portalAccess?.accountEmail ?? portalAccess?.invitedEmail)
      : portalAccess?.invitedEmail
    )?.trim() ?? ""
  );
}

export function ContactPortalStatusIcon({
  portalAccess,
  isWhiteVariant,
}: {
  portalAccess: Contact["portalAccess"];
  isWhiteVariant: boolean;
}) {
  const status = portalAccess?.activeConnection
    ? "active"
    : portalAccess?.status;
  const email = getContactPortalEmail(portalAccess);
  const detail = email ? ` · ${email}` : "";

  if (status === "active") {
    return (
      <PortalStatusIcon
        icon={ShieldCheck}
        label={`Contact portal account active${detail}`}
        className={isWhiteVariant ? "text-muted-foreground" : "text-white/60"}
      />
    );
  }
  if (status === "pending") {
    return (
      <PortalStatusIcon
        icon={Clock3}
        label={`Portal invitation pending${detail}`}
        className={isWhiteVariant ? "text-amber-700" : "text-amber-200"}
      />
    );
  }
  if (status === "expired") {
    return (
      <PortalStatusIcon
        icon={CircleAlert}
        label={`Portal invitation expired${detail}`}
        className={isWhiteVariant ? "text-red-700" : "text-rose-200"}
      />
    );
  }
  return null;
}

function PortalStatusIcon({
  icon: Icon,
  label,
  className,
}: {
  icon: typeof ShieldCheck;
  label: string;
  className: string;
}) {
  return (
    <TooltipWrapper message={label}>
      <span
        role="img"
        aria-label={label}
        tabIndex={0}
        className={cn(
          "inline-flex shrink-0 rounded-sm outline-offset-2",
          className,
        )}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
      </span>
    </TooltipWrapper>
  );
}
